import { 
  addDataSets,
  getDataSetParameters,
  fetchDocSettingsAPI,
  fetchAssistantParametersAPI,
  createPromptAPI,
  createDocSettingsAPI,
  saveAssistantParametersAPI,
  getDataSet
} from '../apis';

// Simple random ID generator
const generateRandomId = () => {
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substring(2, 8);
  return `${timestamp}_${randomStr}`;
};

// Default configuration values
const DEFAULT_QNA_SETTINGS = {
  prompt: 'You are a helpful AI assistant similar to ChatGPT. Provide comprehensive, well-formatted responses using proper markdown formatting. Use emojis and icons (like ✅, 🎯, 💡, 📝, ⚠️, 🔍, etc.) to make your responses more engaging and visually appealing. Structure your responses with headers, bullet points, numbered lists, and code blocks when appropriate. Always aim to be informative, clear, and helpful while maintaining a friendly and professional tone.',
  kValue: 8,
  thresholdValue: 0.4,
  temperature: 0.7,
  modelName: 'gpt-4o-mini',
  maxTokens: 2048,
  topPValue: 0.5,
  responseFormat: 'Text',
  retrievalStrategy: 'Semantic',
  isRerankerEnabled: false,
  showImagesOption: 'fullPages',
  showSource: true,
  questionGeneratorPrompt: 'Generate 2-3 relevant follow-up questions that would help the user explore this topic further. Format them as clickable suggestions.',
  regeneratePrompt: 'Please provide an alternative response with a different approach or perspective. Use proper markdown formatting and include relevant emojis.',
  regenerateWithFeedbackPrompt: 'Please improve the response based on the user feedback. Use proper markdown formatting and include relevant emojis to enhance clarity.',
  clarifyPrompt: 'I need more information to provide the best answer. Could you please clarify or provide more details about your question? 🤔',
  isClarifyPromptEnabled: true,
  agenticQnAEnabled: true, // Enable for regular quick chat only
  agentPrompt: 'You are an intelligent AI agent similar to ChatGPT. Provide comprehensive, well-formatted responses using proper markdown formatting. Use emojis and icons (like ✅, 🎯, 💡, 📝, ⚠️, 🔍, 🚀, 💻, 📊, etc.) to make your responses more engaging and visually appealing. Structure your responses with headers, bullet points, numbered lists, and code blocks when appropriate. Think step-by-step and provide detailed explanations. Always aim to be informative, clear, and helpful while maintaining a friendly and professional tone.',
  tableSchema: ''
};

const DEFAULT_DOC_SETTINGS = {
  chunkSize: 100,
  version: 1,
  chunk: 800,
  embeddingDeployment: "smart-agent-embedding-ada",
  numList: 1,
  chunkOverlap: 320,
  embeddingModel: "text-embedding-ada-002",
  mapSummaryTemplate: "Provide a structured summary of the following content. \n Use the information to create a clear, concise, and brief informative summary that covers all key points, themes, and statistics. \n Do not exceed 3-4 sentences for the entire summary. generate as quickly as possible. \n {content} \n \n Summary:",
  imageSummarizationPrompt: "Act as an image analysis expert. Your task is to assess the content of an image in detail and provide a structured, high-quality summary based on its textual and visual elements.\n\n1. For Text-Heavy Images:\n   - Extract all visible text exactly as it appears without altering spelling.\n   - Analyze structured data, such as tables or lists, interpreting rows and columns with all relevant numbers and their units.\n   - Present a detailed yet concise interpretation of the content, omitting introductory phrases. Ensure that the text flows naturally without phrases like \"Here is the extracted text.\"\n\n2. For Visually Oriented Images:\n   - Summarize high-level information about the visual content, identifying themes, icons, and significant details.\n   - Include numerical data (like percentages, units, measurements) if it's essential for understanding.\n   - Deliver a concise, meaningful summary of key visual elements without going into excessive detail.\n\nGuidelines:\n- Avoid introductory phrases or redundant information.\n- Maintain a clean structure for readability, ensuring that each section is meaningful and relevant.",
  reduceSummaryTemplate: "Below is a collection of summaries based on different sections of a document.\n Use these to produce a single, cohesive summary that thoroughly covers all significant themes, key points, and notable statistics.\n Structure the summary for maximum clarity and flow, using organized sections, bullet points, or paragraphs for readability. \n - Maintain a concise, high-quality flow of information. \n - Limit the final summary to a maximum of 500 words. \n - Ensure the final output is comprehensive,separated paragraphs,well-structured and visually engaging with markdown format. \n\n {doc_summaries} \n\n Final Summary:",
  trainingMethod: "Multi-modal",
  trainingModel: "gpt-4o-mini",
  serviceAgentEnabled: false,
  docChunkEnabled: 0,
  docAsChunkEnabled: false,
  pageAsChunkEnabled: true,
  csvColumns: ""
};

const DEFAULT_ASSISTANT_SETTINGS = {
  assistantInstructions: 'You are a helpful AI assistant similar to ChatGPT. Provide comprehensive, well-formatted responses using proper markdown formatting. Use emojis and icons (like ✅, 🎯, 💡, 📝, ⚠️, 🔍, etc.) to make your responses more engaging and visually appealing. Structure your responses with headers, bullet points, numbered lists, and code blocks when appropriate. Always aim to be informative, clear, and helpful while maintaining a friendly and professional tone.',
  openAIModel: 'gpt-4o-mini',
  temperature: 0.7,
  topPValue: 0.5,
  openAIProjectId: '',
  chunkingStrategy: 'auto',
  serviceAgentEnabled: false,
  codeInterpreterEnabled: false,
  showImagesOption: 'fullPages',
  showSource: true,
  chunk: 800,
  chunkOverlap: 320,
  kValue: 8
};

// createReferenceDatasetConfig function removed - no longer needed
// Backend now handles configuration cloning via isReferenceDataset flag

/**
 * Finds the reference dataset (isReferenceDataset: true)
 * @returns {Promise<Object|null>} Reference dataset or null if not found
 */
const findReferenceDataset = async () => {
  try {
    const response = await getDataSet();
    if (response?.data) {
      return response.data.find(dataset => dataset.isReferenceDataset === true);
    }
    return null;
  } catch (error) {
    console.error('Error finding reference dataset:', error);
    return null;
  }
};

/**
 * Fetches configuration from reference dataset based on its type
 * @param {Object} referenceDataset - Reference dataset object
 * @param {string} quickChatType - Type of QuickChat being created
 * @returns {Promise<Object>} Configuration object
 */
const fetchReferenceConfiguration = async (referenceDataset, quickChatType) => {
  try {
    console.log('Fetching configuration from reference dataset:', referenceDataset.datasetName);
    console.log('Reference dataset type - isAssistantDataset:', referenceDataset.isAssistantDataset);
    console.log('QuickChat type being created:', quickChatType);
    
    const config = {
      qnaSettings: null,
      docSettings: null,
      assistantSettings: null
    };
    
    // Determine which services to call based on reference dataset type and QuickChat type
    const isReferenceAssistant = referenceDataset.isAssistantDataset;
    const isQuickChatAssistant = quickChatType === 'assistantQuickChat';
    
    if (isReferenceAssistant && isQuickChatAssistant) {
      // Reference is Assistant dataset, QuickChat is Assistant - fetch only Assistant settings
      console.log('Fetching Assistant settings only (Assistant → Assistant)');
      try {
        const assistantResponse = await fetchAssistantParametersAPI(referenceDataset.id);
        config.assistantSettings = assistantResponse;
        console.log('✅ Fetched Assistant settings from reference dataset');
      } catch (error) {
        console.log('⚠️ Failed to fetch Assistant settings, using defaults');
        config.assistantSettings = DEFAULT_ASSISTANT_SETTINGS;
      }
      
    } else if (!isReferenceAssistant && !isQuickChatAssistant) {
      // Reference is Normal dataset, QuickChat is Normal - fetch QnA and Document settings
      console.log('Fetching QnA and Document settings (Normal → Normal)');
      const [qnaResponse, docResponse] = await Promise.allSettled([
        getDataSetParameters(referenceDataset.id),
        fetchDocSettingsAPI(referenceDataset.id)
      ]);
      
      // Extract QnA settings
      if (qnaResponse.status === 'fulfilled' && qnaResponse.value?.data) {
        config.qnaSettings = qnaResponse.value.data;
        console.log('✅ Fetched QnA settings from reference dataset');
      } else {
        console.log('⚠️ Failed to fetch QnA settings, using defaults');
        config.qnaSettings = DEFAULT_QNA_SETTINGS;
      }
      
      // Extract Document settings
      if (docResponse.status === 'fulfilled' && docResponse.value?.data) {
        config.docSettings = docResponse.value.data;
        console.log('✅ Fetched Document settings from reference dataset');
      } else {
        console.log('⚠️ Failed to fetch Document settings, using defaults');
        config.docSettings = DEFAULT_DOC_SETTINGS;
      }
      
    } else {
      // Type mismatch - use defaults with warning
      console.log('⚠️ Reference dataset type mismatch with QuickChat type, using defaults');
      console.log(`Reference: ${isReferenceAssistant ? 'Assistant' : 'Normal'}, QuickChat: ${isQuickChatAssistant ? 'Assistant' : 'Normal'}`);
      
      config.qnaSettings = DEFAULT_QNA_SETTINGS;
      config.docSettings = DEFAULT_DOC_SETTINGS;
      config.assistantSettings = DEFAULT_ASSISTANT_SETTINGS;
    }
    
    return config;
    
  } catch (error) {
    console.error('Error fetching reference configuration:', error);
    // Return default configurations if reference fetch fails
    return {
      qnaSettings: DEFAULT_QNA_SETTINGS,
      docSettings: DEFAULT_DOC_SETTINGS,
      assistantSettings: DEFAULT_ASSISTANT_SETTINGS
    };
  }
};

/**
 * Applies configuration to a QuickChat dataset
 * @param {string} datasetId - ID of the created dataset
 * @param {string} type - 'quickChat' or 'assistantQuickChat'
 * @param {Object} config - Configuration object from reference dataset
 */
const applyConfigurationToDataset = async (datasetId, type, config) => {
  try {
    console.log('Applying configuration to dataset:', datasetId);
    
    if (type === 'assistantQuickChat') {
      // For Assistant QuickChat, use Assistant settings
      const assistantPayload = {
        datasetId: datasetId,
        openAIModel: config.assistantSettings.openAIModel,
        temperature: config.assistantSettings.temperature,
        topP: config.assistantSettings.topPValue,
        serviceAgentEnabled: config.assistantSettings.serviceAgentEnabled,
        chunkingStrategy: config.assistantSettings.chunkingStrategy,
        chunk: config.assistantSettings.chunk,
        chunkOverlap: config.assistantSettings.chunkOverlap,
        systemInstructions: config.assistantSettings.assistantInstructions,
        codeInterpreterEnabled: config.assistantSettings.codeInterpreterEnabled,
        showImagesOption: config.assistantSettings.showImagesOption,
        showSource: config.assistantSettings.showSource,
        kValue: config.assistantSettings.kValue
      };
      
      await saveAssistantParametersAPI(datasetId, assistantPayload);
      console.log('Applied Assistant settings to QuickChat dataset');
      
    } else {
      // For regular QuickChat, use QnA and Document settings
      
      // Apply QnA settings (prompt parameters)
      const qnaPayload = {
        prompt: config.qnaSettings.prompt,
        kValue: config.qnaSettings.kValue,
        thresholdValue: config.qnaSettings.thresholdValue,
        temperature: config.qnaSettings.temperature,
        modelName: config.qnaSettings.modelName,
        maxTokens: config.qnaSettings.maxTokens,
        topPValue: config.qnaSettings.topPValue,
        responseFormat: config.qnaSettings.responseFormat,
        retrievalStrategy: config.qnaSettings.retrievalStrategy,
        isRerankerEnabled: config.qnaSettings.isRerankerEnabled,
        showImagesOption: config.qnaSettings.showImagesOption,
        showSource: config.qnaSettings.showSource,
        questionGeneratorPrompt: config.qnaSettings.questionGeneratorPrompt,
        regeneratePrompt: config.qnaSettings.regeneratePrompt,
        regenerateWithFeedbackPrompt: config.qnaSettings.regenerateWithFeedbackPrompt,
        clarifyPrompt: config.qnaSettings.clarifyPrompt,
        isClarifyPromptEnabled: config.qnaSettings.isClarifyPromptEnabled,
        agenticQnAEnabled: config.qnaSettings.agenticQnAEnabled,
        agentPrompt: config.qnaSettings.agentPrompt,
        tableSchema: config.qnaSettings.tableSchema
      };
      
      await createPromptAPI(qnaPayload, datasetId);
      console.log('Applied QnA settings to QuickChat dataset');
      
      // Apply Document settings
      const docPayload = {
        chunkSize: config.docSettings.chunkSize,
        version: config.docSettings.version,
        chunk: config.docSettings.chunk,
        embeddingDeployment: config.docSettings.embeddingDeployment,
        numList: config.docSettings.numList,
        chunkOverlap: config.docSettings.chunkOverlap,
        embeddingModel: config.docSettings.embeddingModel,
        mapSummaryTemplate: config.docSettings.mapSummaryTemplate,
        imageSummarizationPrompt: config.docSettings.imageSummarizationPrompt,
        reduceSummaryTemplate: config.docSettings.reduceSummaryTemplate,
        trainingMethod: config.docSettings.trainingMethod,
        trainingModel: config.docSettings.trainingModel,
        serviceAgentEnabled: config.docSettings.serviceAgentEnabled,
        docChunkEnabled: config.docSettings.docChunkEnabled,
        docAsChunkEnabled: config.docSettings.docAsChunkEnabled,
        pageAsChunkEnabled: config.docSettings.pageAsChunkEnabled,
        csvColumns: config.docSettings.csvColumns
      };
      
      await createDocSettingsAPI(docPayload, datasetId);
      console.log('Applied Document settings to QuickChat dataset');
    }
    
  } catch (error) {
    console.error('Error applying configuration to dataset:', error);
    throw error;
  }
};

/**
 * Creates a Quick Chat dataset with configurations from reference dataset
 * @param {string} type - 'quickChat' or 'assistantQuickChat'
 * @returns {Promise<{datasetId: string, datasetName: string}>}
 */
export const createQuickChatDataset = async (type = 'quickChat') => {
  try {
    const randomId = generateRandomId();
    const datasetName = `${type}_${randomId}`;
    
    // Step 1: Create dataset
    const datasetPayload = {
      datasetName,
      description: `Auto-generated dataset for ${type}`,
      isAssistantDataset: type === 'assistantQuickChat',
      isQuickChat: true,
      isReferenceDataset: false // QuickChat datasets are not reference datasets by default
    };
    
    console.log('Creating dataset with payload:', datasetPayload);
    const datasetResponse = await addDataSets(datasetPayload);
    
    if (!datasetResponse?.data?.id) {
      throw new Error('Failed to create dataset: Invalid response');
    }
    
    const datasetId = datasetResponse.data.id;
    console.log('Dataset created with ID:', datasetId);
    
    // Step 2: Find reference dataset and fetch its configuration
    const referenceDataset = await findReferenceDataset();
    
    if (referenceDataset) {
      console.log('Found reference dataset:', referenceDataset.datasetName);
      
      // Fetch configuration from reference dataset
      const config = await fetchReferenceConfiguration(referenceDataset, type);
      
      // Apply configuration to the new QuickChat dataset
      await applyConfigurationToDataset(datasetId, type, config);
      
      console.log('Successfully applied reference dataset configuration to QuickChat');
    } else {
      console.log('No reference dataset found, applying default configuration');
      
      // Apply default configuration if no reference dataset exists
      const defaultConfig = {
        qnaSettings: DEFAULT_QNA_SETTINGS,
        docSettings: DEFAULT_DOC_SETTINGS,
        assistantSettings: DEFAULT_ASSISTANT_SETTINGS
      };
      
      await applyConfigurationToDataset(datasetId, type, defaultConfig);
    }
    
    console.log('Quick chat dataset created successfully:', { datasetId, datasetName });
    return { datasetId, datasetName };
    
  } catch (error) {
    console.error('Error creating quick chat dataset:', error);
    throw new Error(`Failed to create ${type} dataset: ${error.message}`);
  }
};

/**
 * Creates a standard Quick Chat dataset
 * @returns {Promise<{datasetId: string, datasetName: string}>}
 */
export const createQuickChat = () => {
  return createQuickChatDataset('quickChat');
};

/**
 * Creates an Assistant Quick Chat dataset
 * @returns {Promise<{datasetId: string, datasetName: string}>}
 */
export const createAssistantQuickChat = () => {
  return createQuickChatDataset('assistantQuickChat');
};

// Reference dataset management is now handled via isReferenceDataset flag
// No need for localStorage-based configuration management

export default {
  createQuickChatDataset,
  createQuickChat,
  createAssistantQuickChat
};